// frontend/src/pages/asset-admin/AssetAdminFleetMaintenance.js
/**
 * Asset Admin — Fleet Maintenance Analytics
 *
 * Fleet-wide rollups over the cleaned maintenance data (normalized ServicePart
 * components from the workshop service logs). From an Ops manager's lens:
 *   1. What drives workshop visits?    → Spare-parts demand (visits + vehicles)
 *   2. Which part keeps coming back?   → Repeat repairs (non-routine, ≤90 days)
 *   3. How much routine servicing?     → Service cadence (scheduled volume)
 *   4. What's each vehicle's profile?  → Per-vehicle service table (click → detail)
 *
 * Data: GET /asset-inventory/fleet-vehicles/maintenance-analytics/?year=
 * Theme: Red (Admin). Follows the design-system-reference metric/panel patterns.
 */

import React, { useState } from 'react';
import {
  Wrench, Truck, Gauge, AlertTriangle, RotateCcw, ListChecks,
  AlertCircle, Calendar, ChevronRight,
} from 'lucide-react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell,
} from 'recharts';
import { useFleetMaintenanceAnalytics } from '../../hooks/useAssetInventoryData';
import { useAssetDetail } from '../../contexts/AssetDetailContext';
import { formatDate } from '../../utils/dateUtils';
import PillSelect from '../../components/UI/PillSelect';

const formatInt = (v) =>
  v === null || v === undefined ? '—' : Number(v).toLocaleString('en-SG');

// Demand bars cycle through the admin-friendly palette.
const BAR_COLORS = ['#dc2626', '#ea580c', '#d97706', '#ca8a04', '#65a30d', '#0891b2', '#2563eb', '#7c3aed'];

const MetricCard = ({ title, value, subtitle, icon: Icon, color = 'red' }) => {
  const colorClasses = {
    red: 'bg-red-100 text-red-600',
    green: 'bg-green-100 text-green-600',
    blue: 'bg-blue-100 text-blue-600',
    amber: 'bg-amber-100 text-amber-600',
    purple: 'bg-purple-100 text-purple-600',
  };
  return (
    <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
      <div className="flex items-center">
        <div className={`w-9 h-9 sm:w-12 sm:h-12 rounded-full flex items-center justify-center flex-shrink-0 ${colorClasses[color]}`}>
          <Icon className="w-5 h-5 sm:w-6 sm:h-6" />
        </div>
        <div className="ml-2 sm:ml-4 min-w-0">
          <p className="text-lg sm:text-2xl font-bold text-gray-900">{value}</p>
          <p className="text-xs sm:text-sm text-gray-600 truncate">{title}</p>
          {subtitle && <p className="text-xs text-gray-400 mt-0.5 truncate">{subtitle}</p>}
        </div>
      </div>
    </div>
  );
};

const Panel = ({ title, icon: Icon, children, action }) => (
  <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
    <div className="flex items-center justify-between gap-2 mb-4">
      <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-700">
        {Icon && <Icon className="w-4 h-4 text-gray-400" />}
        {title}
      </h3>
      {action}
    </div>
    {children}
  </div>
);

const PageSkeleton = () => (
  <div className="space-y-6 bg-gray-50 min-h-screen animate-pulse">
    <div className="bg-white rounded-2xl shadow-lg p-6 h-20" />
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      {[0, 1, 2, 3].map((i) => <div key={i} className="bg-white rounded-2xl border h-24" />)}
    </div>
    <div className="bg-white rounded-2xl border h-80" />
  </div>
);

const AssetAdminFleetMaintenance = () => {
  const [year, setYear] = useState('all');
  const params = year === 'all' ? {} : { year };
  const { data, isLoading, error } = useFleetMaintenanceAnalytics(params);
  const { openAssetDetail } = useAssetDetail();

  if (isLoading) return <PageSkeleton />;

  if (error) {
    return (
      <div className="bg-gray-50 min-h-screen">
        <div className="bg-red-50 border border-red-200 rounded-2xl p-6 text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
          <p className="text-red-700 font-medium">Failed to load fleet maintenance analytics</p>
          <p className="text-red-600 text-sm mt-1">Please refresh the page to try again.</p>
        </div>
      </div>
    );
  }

  const summary = data?.summary || {};
  const componentDemand = data?.component_demand || [];
  const byVehicle = data?.by_vehicle || [];
  const repeatRepairs = data?.repeat_repairs || [];
  const serviceCadence = data?.service_cadence || [];
  const dateRange = summary.date_range || {};

  const chartData = componentDemand.slice(0, 12).map((c) => ({
    name: c.name,
    visits: c.visits,
    vehicles: c.vehicles,
  }));

  // Year options: from earliest record to current year.
  const currentYear = new Date().getFullYear();
  const firstYear = dateRange.from ? Number(dateRange.from.slice(0, 4)) : currentYear;
  const yearOptions = [];
  for (let y = currentYear; y >= firstYear; y--) yearOptions.push(y);

  const maxCadence = Math.max(1, ...serviceCadence.map((c) => c.visits));

  return (
    <div className="space-y-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-5 lg:p-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3 sm:gap-4 min-w-0 flex-1">
            <div className="w-9 h-9 sm:w-12 sm:h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
              <Wrench className="w-4 h-4 sm:w-6 sm:h-6 text-red-600" />
            </div>
            <div className="min-w-0 flex-1">
              <h1 className="text-base sm:text-xl font-bold text-gray-900 truncate">Fleet Maintenance</h1>
              <p className="text-xs sm:text-sm text-gray-500 mt-1 truncate">
                Component demand &amp; service history across the fleet
              </p>
            </div>
          </div>
          <PillSelect
            options={[
              { value: 'all', label: 'All time' },
              ...yearOptions.map((y) => ({ value: String(y), label: String(y) })),
            ]}
            value={year}
            onChange={setYear}
            icon={Calendar}
            iconColor="text-red-600"
            theme="admin"
            minWidth="min-w-[140px]"
          />
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <MetricCard
          title="Vehicles Serviced"
          value={summary.vehicles_with_history || 0}
          subtitle="With service history"
          icon={Truck}
          color="blue"
        />
        <MetricCard
          title="Workshop Visits"
          value={formatInt(summary.total_visits)}
          subtitle={dateRange.from ? `${formatDate(dateRange.from)} – ${formatDate(dateRange.to)}` : '—'}
          icon={Wrench}
          color="red"
        />
        <MetricCard
          title="Components Logged"
          value={formatInt(summary.total_components_logged)}
          subtitle={`${summary.visits_with_components || 0} visits itemized`}
          icon={ListChecks}
          color="green"
        />
        <MetricCard
          title="Repeat Repairs"
          value={repeatRepairs.length}
          subtitle="Same part back ≤90 days (non-routine)"
          icon={AlertTriangle}
          color="amber"
        />
      </div>

      {/* Component demand */}
      <Panel title="Spare-Parts Demand (by workshop visits)" icon={Wrench}>
        {chartData.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-8">No component data yet.</p>
        ) : (
          <ResponsiveContainer width="100%" height={Math.max(240, chartData.length * 34)}>
            <BarChart data={chartData} layout="vertical" margin={{ left: 8, right: 24, top: 4, bottom: 4 }}>
              <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
              <YAxis
                type="category"
                dataKey="name"
                width={150}
                tick={{ fontSize: 11 }}
                interval={0}
              />
              <Tooltip
                formatter={(value, name) => [value, name === 'visits' ? 'Visits' : name]}
                cursor={{ fill: 'rgba(0,0,0,0.04)' }}
              />
              <Bar dataKey="visits" radius={[0, 4, 4, 0]}>
                {chartData.map((entry, i) => (
                  <Cell key={entry.name} fill={BAR_COLORS[i % BAR_COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </Panel>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Repeat repairs — genuine "came back too soon" quality signal */}
        <Panel title="Repeat Repairs (came back ≤90 days)" icon={AlertTriangle}>
          {repeatRepairs.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-8">
              No repeat repairs flagged — no non-routine part returned within 90 days.
            </p>
          ) : (
            <ul className="space-y-1.5 max-h-80 overflow-y-auto pr-1">
              {repeatRepairs.slice(0, 30).map((r, idx) => {
                const urgent = r.min_gap_days <= 14;
                return (
                  <li key={`${r.asset_id}-${r.code}-${idx}`}>
                    <button
                      type="button"
                      onClick={() => r.asset_id && openAssetDetail(r.asset_id)}
                      className="w-full flex items-center justify-between gap-2 rounded-lg border border-gray-100 px-3 py-2 text-left hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-1"
                    >
                      <div className="min-w-0">
                        <div className="flex items-baseline gap-2">
                          <span className="text-sm font-semibold text-gray-900">{r.vehicle_number}</span>
                          {r.brand_model && (
                            <span className="text-xs text-gray-400 truncate">{r.brand_model}</span>
                          )}
                        </div>
                        <div className="mt-0.5 flex items-center gap-1.5 text-xs text-gray-500">
                          <span className="truncate">{r.component}</span>
                          {r.last_occurrence && (
                            <>
                              <span className="text-gray-300">·</span>
                              <span className="whitespace-nowrap text-gray-400">last {formatDate(r.last_occurrence)}</span>
                            </>
                          )}
                        </div>
                      </div>
                      <span
                        className={`ml-2 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold whitespace-nowrap ${
                          urgent ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'
                        }`}
                        title={`Re-serviced ${r.visits}× — tightest turnaround ${r.min_gap_days} days`}
                      >
                        {r.visits}× · {r.min_gap_days}d
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </Panel>

        {/* Service cadence — neutral routine/scheduled servicing volume */}
        <Panel title="Routine Service Cadence" icon={RotateCcw}>
          {serviceCadence.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-8">No routine servicing logged.</p>
          ) : (
            <ul className="space-y-2.5 max-h-80 overflow-y-auto pr-1">
              {serviceCadence.map((c) => (
                <li key={c.code}>
                  <div className="flex items-center justify-between text-sm mb-0.5">
                    <span className="text-gray-700 truncate pr-2">{c.name}</span>
                    <span className="text-gray-500 font-medium whitespace-nowrap">
                      {c.visits} visit{c.visits !== 1 ? 's' : ''} · {c.vehicles} veh
                    </span>
                  </div>
                  <div className="h-1.5 rounded-full bg-gray-100 overflow-hidden">
                    <div
                      className="h-full bg-blue-500/70 rounded-full"
                      style={{ width: `${(c.visits / maxCadence) * 100}%` }}
                    />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>

      {/* Per-vehicle table */}
      <Panel title={`Per-Vehicle Service Profile (${byVehicle.length})`} icon={Truck}>
        {byVehicle.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-8">No vehicles with service history.</p>
        ) : (
          <>
            {/* Mobile: cards */}
            <div className="space-y-3 md:hidden">
              {byVehicle.map((v) => (
                <button
                  key={v.vehicle_id}
                  type="button"
                  onClick={() => v.asset_id && openAssetDetail(v.asset_id)}
                  className="w-full text-left p-4 rounded-xl border border-gray-100 bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-1"
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-gray-900 truncate">{v.vehicle_number}</p>
                      {v.brand_model && <p className="text-xs text-gray-400 truncate">{v.brand_model}</p>}
                    </div>
                    <span className="flex-shrink-0 text-xs text-gray-500">{v.visit_count} visits</span>
                  </div>
                  <div className="flex flex-wrap gap-1 mb-2">
                    {v.top_components.map((c) => (
                      <span key={c.name} className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-[11px] text-gray-700">
                        {c.name}<span className="text-[9px] font-semibold text-gray-500">×{c.count}</span>
                      </span>
                    ))}
                  </div>
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <span className="flex items-center gap-1"><Gauge className="w-3 h-3" />
                      {v.current_mileage != null ? `${formatInt(v.current_mileage)} km` : '—'}</span>
                    <span>Last: {formatDate(v.last_service)}</span>
                  </div>
                </button>
              ))}
            </div>

            {/* Desktop: table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    <th className="px-4 py-3">Vehicle</th>
                    <th className="px-4 py-3 text-right">Visits</th>
                    <th className="px-4 py-3 text-right whitespace-nowrap">Mileage</th>
                    <th className="px-4 py-3 whitespace-nowrap">Last Service</th>
                    <th className="px-4 py-3">Top Components</th>
                    <th className="px-4 py-3" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {byVehicle.map((v) => (
                    <tr
                      key={v.vehicle_id}
                      onClick={() => v.asset_id && openAssetDetail(v.asset_id)}
                      className="hover:bg-gray-50 cursor-pointer transition-colors"
                    >
                      <td className="px-4 py-3">
                        <div className="font-semibold text-gray-900 whitespace-nowrap">{v.vehicle_number}</div>
                        {v.brand_model && <div className="text-xs text-gray-400">{v.brand_model}</div>}
                      </td>
                      <td className="px-4 py-3 text-right font-medium text-gray-700">{v.visit_count}</td>
                      <td className="px-4 py-3 text-right text-gray-600 whitespace-nowrap">
                        {v.current_mileage != null ? `${formatInt(v.current_mileage)} km` : '—'}
                      </td>
                      <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{formatDate(v.last_service)}</td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {v.top_components.map((c) => (
                            <span
                              key={c.name}
                              className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-[11px] text-gray-700"
                            >
                              {c.name}
                              <span className="text-[9px] font-semibold text-gray-500">×{c.count}</span>
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <ChevronRight className="w-4 h-4 text-gray-300 inline" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </Panel>
    </div>
  );
};

export default AssetAdminFleetMaintenance;
